/**
 * PiPOverlays — positions all PiP camera panels as HTML overlays on top of
 * the main viewer canvas.
 *
 * This component is rendered as a sibling to <SceneCanvas> inside the same
 * relative container, following the same pattern as ViewerToolbar.
 *
 * The `rightExclusionPx` prop specifies a pixel width on the right side where
 * PiP panels must not intrude (e.g. the parameter panel in product mode).
 * PiP panels that would overlap this zone are shifted/clipped accordingly via
 * the container's `padding-right`.
 *
 * Drag-and-drop: when the user starts dragging a panel via its handle, this
 * component renders a slot-grid overlay showing all valid snap targets.
 * On release, the panel snaps to the nearest slot and `onReplaceCode` is
 * called to update the source x/y coordinates in the editor.
 *
 * Pixel mode: when a PiP's x/y/width/height source value has |value| > 1 it
 * is interpreted as pixels (stored in xPx/yPx/widthPx/heightPx on PiPCamera).
 * - Pixel-positioned panels drag freely (no slot-grid snap) and write integer
 *   pixel values back to source on drop.
 * - Pixel-sized panels write integer pixel values back on resize release.
 */

import { useState, useCallback, useRef } from 'react';
import * as THREE from 'three';
import type { PiPCamera, SceneLight } from '@/worker/types';
import type { ColoredGeometry } from '@/components/viewer/types';
import type { SceneSettings } from '@/contexts/SceneSettingsContext';
import { PiPPanel } from './PiPPanel';
import type { PiPParamChanges } from './PiPContextMenu';

// ---------------------------------------------------------------------------
// Shared slot-grid geometry constants (also used by ThreeScene's findFreeSlot)
// ---------------------------------------------------------------------------

export const PIP_W = 0.3;
export const PIP_H = 0.2;
export const PIP_GAP = 0.02;
export const PIP_COL_STEP = PIP_W + PIP_GAP;
export const PIP_ROW_STEP = PIP_H + PIP_GAP;

// ---------------------------------------------------------------------------
// Negative-anchor coordinate helpers
// ---------------------------------------------------------------------------

/**
 * Convert a stored x value (possibly negative = right-anchored) to an
 * absolute normalized left position (0–1) for rendering and hit-testing.
 * Negative x means the panel's right edge is |x| from the viewport right:
 *   left = 1 - |x| - width
 */
export function resolveLeft(x: number, w: number): number {
  return x >= 0 ? x : 1 + x - w;
}

/**
 * Convert a stored y value (possibly negative = bottom-anchored) to an
 * absolute normalized top position (0–1).
 * Negative y means the panel's bottom edge is |y| from the viewport bottom:
 *   top = 1 - |y| - height
 */
export function resolveTop(y: number, h: number): number {
  return y >= 0 ? y : 1 + y - h;
}

/**
 * Convert an absolute normalized left position back to a stored x value,
 * preserving the anchor side (negative = right-anchored).
 *   negX=true  →  x = left + width - 1  (negative)
 *   negX=false →  x = left             (positive)
 */
function unresolveX(left: number, w: number, negX: boolean): number {
  return negX ? left + w - 1 : left;
}

/** Same for y / top. */
function unresolveY(top: number, h: number, negY: boolean): number {
  return negY ? top + h - 1 : top;
}

// ---------------------------------------------------------------------------
// Pixel-mode helpers
// ---------------------------------------------------------------------------

/**
 * Resolve a PiPCamera's x position to an absolute normalized left (0–1),
 * using pixel values when available (requires container width in px).
 * Falls back to the fraction-based resolveLeft for non-pixel panels.
 */
export function resolveLeftPx(
  cam: { x: number; xPx?: number; width: number; widthPx?: number },
  containerW: number,
): number {
  if (cam.xPx !== undefined && containerW > 0) {
    const wPx = cam.widthPx ?? cam.width * containerW;
    return cam.xPx >= 0
      ? cam.xPx / containerW
      : (containerW + cam.xPx - wPx) / containerW;
  }
  return resolveLeft(cam.x, cam.width);
}

/**
 * Resolve a PiPCamera's y position to an absolute normalized top (0–1),
 * using pixel values when available (requires container height in px).
 */
export function resolveTopPx(
  cam: { y: number; yPx?: number; height: number; heightPx?: number },
  containerH: number,
): number {
  if (cam.yPx !== undefined && containerH > 0) {
    const hPx = cam.heightPx ?? cam.height * containerH;
    return cam.yPx >= 0
      ? cam.yPx / containerH
      : (containerH + cam.yPx - hPx) / containerH;
  }
  return resolveTop(cam.y, cam.height);
}

/**
 * Returns the effective panel width in normalized units (0–1),
 * using widthPx if set.
 */
function resolveWidthNorm(
  cam: { width: number; widthPx?: number },
  containerW: number,
): number {
  if (cam.widthPx !== undefined && containerW > 0)
    return cam.widthPx / containerW;
  return cam.width;
}

/**
 * Returns the effective panel height in normalized units (0–1),
 * using heightPx if set.
 */
function resolveHeightNorm(
  cam: { height: number; heightPx?: number },
  containerH: number,
): number {
  if (cam.heightPx !== undefined && containerH > 0)
    return cam.heightPx / containerH;
  return cam.height;
}

// ---------------------------------------------------------------------------
// Slot grid helpers
// ---------------------------------------------------------------------------

/**
 * All snap slots that fit within the 0–1 normalized viewport for a given PiP
 * size and anchor sides. Returned x/y values use the same sign convention as
 * the source (negative = right/bottom-anchored).
 *
 * negX=true: columns are generated from the right edge; returned x < 0.
 * negY=true: rows are generated from the bottom edge; returned y < 0.
 */
export function buildSlots(
  w = PIP_W,
  h = PIP_H,
  negX = false,
  negY = false,
): Array<{ x: number; y: number }> {
  const slots: Array<{ x: number; y: number }> = [];
  const colStep = w + PIP_GAP;
  const rowStep = h + PIP_GAP;
  const maxCols = Math.floor((1 - PIP_GAP) / colStep);
  const maxRows = Math.floor((1 - PIP_GAP) / rowStep);
  for (let row = 0; row < maxRows; row++) {
    for (let col = 0; col < maxCols; col++) {
      // Negative anchors: col/row 0 is closest to the opposite edge, stepping inward
      const absLeft = negX
        ? 1 - PIP_GAP - w - col * colStep
        : PIP_GAP + col * colStep;
      const absTop = negY
        ? 1 - PIP_GAP - h - row * rowStep
        : PIP_GAP + row * rowStep;
      slots.push({
        x: negX ? absLeft + w - 1 : absLeft, // unresolve to stored sign
        y: negY ? absTop + h - 1 : absTop,
      });
    }
  }
  return slots;
}

/** Return the slot nearest to the given panel top-left (in absolute 0–1 space). */
export function nearestSlot(
  nx: number,
  ny: number,
  w = PIP_W,
  h = PIP_H,
  negX = false,
  negY = false,
): { x: number; y: number } {
  const slots = buildSlots(w, h, negX, negY);
  let best = slots[0];
  let bestDist = Infinity;
  for (const s of slots) {
    // Compare slot centers to the given point (nx/ny is already the panel center)
    const scx = resolveLeft(s.x, w) + w / 2;
    const scy = resolveTop(s.y, h) + h / 2;
    const d = (scx - nx) ** 2 + (scy - ny) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Slot grid overlay — shown while any panel is being dragged
// ---------------------------------------------------------------------------

function SlotGrid({
  hoveredSlot,
  occupiedSlots,
  pipW,
  pipH,
  negX,
  negY,
}: {
  hoveredSlot: { x: number; y: number } | null;
  /** Slots occupied by panels that are NOT currently being dragged (absolute left/top) */
  occupiedSlots: Array<{ left: number; top: number }>;
  /** Width of the PiP being dragged (normalized 0–1) */
  pipW: number;
  /** Height of the PiP being dragged (normalized 0–1) */
  pipH: number;
  /** Whether the dragged PiP is right-anchored */
  negX: boolean;
  /** Whether the dragged PiP is bottom-anchored */
  negY: boolean;
}) {
  const slots = buildSlots(pipW, pipH, negX, negY);
  return (
    <>
      {slots.map((s, i) => {
        // Resolve stored slot coords to absolute position for rendering
        const sl = resolveLeft(s.x, pipW);
        const st = resolveTop(s.y, pipH);
        const isHovered =
          hoveredSlot !== null &&
          Math.abs(hoveredSlot.x - s.x) < 0.001 &&
          Math.abs(hoveredSlot.y - s.y) < 0.001;
        const isOccupied = occupiedSlots.some(
          (o) => Math.abs(o.left - sl) < 0.001 && Math.abs(o.top - st) < 0.001,
        );
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${sl * 100}%`,
              top: `${st * 100}%`,
              width: `${pipW * 100}%`,
              height: `${pipH * 100}%`,
              borderRadius: '8px',
              border: isHovered
                ? '2px solid rgba(255,255,255,0.85)'
                : '1px dashed rgba(255,255,255,0.25)',
              background: isHovered
                ? 'rgba(255,255,255,0.08)'
                : isOccupied
                  ? 'rgba(255,255,255,0.02)'
                  : 'transparent',
              boxSizing: 'border-box',
              transition: 'border-color 0.1s ease, background 0.1s ease',
              pointerEvents: 'none',
            }}
          />
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// PiPOverlays
// ---------------------------------------------------------------------------

export interface DragState {
  /** Index of the camera being dragged */
  index: number;
  /** Current normalized position of the panel top-left while dragging */
  nx: number;
  ny: number;
  /** True when the dragged panel uses pixel positioning (xPx/yPx set in source) */
  pxMode: boolean;
}

export interface ResizeState {
  /** Index of the camera being resized */
  index: number;
  /** Live normalized width while resizing */
  width: number;
  /** Live normalized height while resizing */
  height: number;
  /** True when the panel uses pixel sizing (widthPx/heightPx set in source) */
  pxMode: boolean;
}

interface PiPOverlaysProps {
  cameras: PiPCamera[];
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  color: string;
  backgroundColor: string;
  /** Full resolved scene settings forwarded to each PiP panel for lighting + bloom */
  sceneSettings: SceneSettings;
  /** Custom lights from OpenSCAD code, forwarded to each PiP panel */
  lights: SceneLight[];
  /**
   * Pixel width on the right edge that PiP panels must not intrude into
   * (e.g. the parameter panel in product mode). Defaults to 0.
   */
  rightExclusionPx?: number;
  /**
   * Called when the user clicks a PiP panel (not after a drag).
   * The main camera should animate to the clicked panel's position/target.
   */
  onCameraClick?: (camera: PiPCamera) => void;
  /**
   * Called when a PiP panel is dropped onto a new slot.
   * Should do a find-replace in the editor source to update x/y.
   */
  onReplaceCode?: (search: string | RegExp, replacement: string) => void;
}

export function PiPOverlays({
  cameras,
  geometry,
  coloredGeometries,
  color,
  backgroundColor,
  sceneSettings,
  lights,
  rightExclusionPx = 0,
  onCameraClick,
  onReplaceCode,
}: PiPOverlaysProps) {
  // Drag state: which panel is being dragged and its live normalized position
  const [dragState, setDragState] = useState<DragState | null>(null);
  // The slot the drag is currently hovering over (for highlighting)
  const [hoveredSlot, setHoveredSlot] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Resize state: which panel is being resized and its live dimensions
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);

  // Ref to the container div so we can convert pixel coords to normalized
  const containerRef = useRef<HTMLDivElement>(null);
  // Ref to the last snapped slot during a drag — used by onUp to avoid
  // re-deriving from pointer coordinates (which may be unreliable under
  // pointer capture on some browsers).
  const snappedSlotRef = useRef<{ x: number; y: number } | null>(null);
  // Ref to the last snapped size during a resize — same rationale as snappedSlotRef.
  const snappedSizeRef = useRef<{ width: number; height: number } | null>(null);

  /**
   * Called by PiPPanel's drag handle on pointerdown.
   * We take over from here and track pointermove/pointerup on the window.
   *
   * Pixel-mode panels (xPx/yPx set): drag freely, no slot-grid snap, write
   * integer px values back to source on drop.
   * Fraction-mode panels: snap to the nearest slot grid cell as before.
   */
  const handleDragStart = useCallback(
    (index: number, startEvent: PointerEvent) => {
      if (!onReplaceCode) return;

      const cam = cameras[index];
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const containerW = rect.width;
      const containerH = rect.height;

      // Whether this panel uses pixel positioning
      const isPxX = cam.xPx !== undefined;
      const isPxY = cam.yPx !== undefined;
      const isPxPos = isPxX || isPxY;

      // Normalized panel dimensions for calculations (use px→fraction if needed)
      const pipW = resolveWidthNorm(cam, containerW);
      const pipH = resolveHeightNorm(cam, containerH);

      // Whether this PiP uses right/bottom anchoring in source (fraction mode)
      const negX = cam.x < 0;
      const negY = cam.y < 0;

      // Resolve stored x/y to absolute 0–1 positions for pixel calculations
      const absLeft = resolveLeftPx(cam, containerW);
      const absTop = resolveTopPx(cam, containerH);

      // Offset of pointer inside the panel (so it drags from where you grabbed)
      const panelPixelLeft = absLeft * containerW + rect.left;
      const panelPixelTop = absTop * containerH + rect.top;
      const offsetX = startEvent.clientX - panelPixelLeft;
      const offsetY = startEvent.clientY - panelPixelTop;

      if (isPxPos) {
        // ── Pixel-mode drag: free movement, no slot grid ──────────────────
        setDragState({ index, nx: absLeft, ny: absTop, pxMode: true });
        setHoveredSlot(null);

        const onMove = (e: PointerEvent) => {
          const newLeft = e.clientX - offsetX - rect.left;
          const newTop = e.clientY - offsetY - rect.top;
          const nx = Math.max(0, Math.min(1 - pipW, newLeft / containerW));
          const ny = Math.max(0, Math.min(1 - pipH, newTop / containerH));
          setDragState((prev) => (prev ? { ...prev, nx, ny } : prev));
        };

        const onUp = (e: PointerEvent) => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);

          const finalLeft = e.clientX - offsetX - rect.left;
          const finalTop = e.clientY - offsetY - rect.top;
          // Clamp to container bounds in pixels
          const newLeftPx = Math.round(
            Math.max(0, Math.min(containerW - pipW * containerW, finalLeft)),
          );
          const newTopPx = Math.round(
            Math.max(0, Math.min(containerH - pipH * containerH, finalTop)),
          );

          // Determine anchor sign: preserve original anchor side from source
          const newXPx = isPxX
            ? cam.xPx! < 0
              ? -(containerW - newLeftPx - pipW * containerW)
              : newLeftPx
            : newLeftPx;
          const newYPx = isPxY
            ? cam.yPx! < 0
              ? -(containerH - newTopPx - pipH * containerH)
              : newTopPx
            : newTopPx;

          const oldXRaw = cam.xPx ?? cam.x;
          const oldYRaw = cam.yPx ?? cam.y;
          const newXRaw = Math.round(newXPx);
          const newYRaw = Math.round(newYPx);

          if (newXRaw !== oldXRaw || newYRaw !== oldYRaw) {
            const escapedX = String(oldXRaw).replace('.', '\\.');
            const escapedY = String(oldYRaw).replace('.', '\\.');
            const pattern = new RegExp(
              `(camera_pip\\([^)]*?)x=${escapedX},\\s*y=${escapedY}`,
              's',
            );
            onReplaceCode(pattern, `$1x=${newXRaw}, y=${newYRaw}`);
          }

          setDragState(null);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      } else {
        // ── Fraction-mode drag: snap to slot grid ─────────────────────────
        // Initial snap uses same-side grid (pass panel center)
        const initialSlot = nearestSlot(
          absLeft + pipW / 2,
          absTop + pipH / 2,
          pipW,
          pipH,
          negX,
          negY,
        );
        snappedSlotRef.current = initialSlot;
        setDragState({ index, nx: absLeft, ny: absTop, pxMode: false });
        setHoveredSlot(initialSlot);

        const snapNearest = (
          nx: number,
          ny: number,
        ): { x: number; y: number } =>
          nearestSlot(nx + pipW / 2, ny + pipH / 2, pipW, pipH, negX, negY);

        const onMove = (e: PointerEvent) => {
          const newLeft = e.clientX - offsetX - rect.left;
          const newTop = e.clientY - offsetY - rect.top;
          const nx = Math.max(0, Math.min(1 - pipW, newLeft / containerW));
          const ny = Math.max(0, Math.min(1 - pipH, newTop / containerH));
          setDragState((prev) => (prev ? { ...prev, nx, ny } : prev));
          const best = snapNearest(nx, ny);
          snappedSlotRef.current = best;
          setHoveredSlot(best);
        };

        const onUp = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);

          const snapped =
            snappedSlotRef.current ??
            nearestSlot(
              absLeft + pipW / 2,
              absTop + pipH / 2,
              pipW,
              pipH,
              negX,
              negY,
            );

          const r2 = (n: number) => Math.round(n * 100) / 100;
          const oldX = r2(cam.x);
          const oldY = r2(cam.y);
          const newX = r2(snapped.x);
          const newY = r2(snapped.y);

          if (oldX !== newX || oldY !== newY) {
            const escapedX = String(oldX).replace('.', '\\.');
            const escapedY = String(oldY).replace('.', '\\.');
            const pattern = new RegExp(
              `(camera_pip\\([^)]*?)x=${escapedX},\\s*y=${escapedY}`,
              's',
            );
            onReplaceCode(pattern, `$1x=${newX}, y=${newY}`);
          }

          snappedSlotRef.current = null;
          setDragState(null);
          setHoveredSlot(null);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      }
    },
    [cameras, onReplaceCode],
  );

  /**
   * Apply context-menu parameter changes to the editor source.
   *
   * Strategy: identify the specific camera_pip(…) block by its current x and y
   * values (slot-grid coords are unique per panel), then replace only the
   * changed parameters inside that block. Each parameter is replaced with its
   * own targeted regex so unrelated parameters are untouched.
   */
  const handleApplyChanges = useCallback(
    (cam: PiPCamera, changes: PiPParamChanges) => {
      if (!onReplaceCode) return;

      const r2 = (n: number) => Math.round(n * 100) / 100;
      const esc = (n: number) => String(r2(n)).replace('.', '\\.');

      // Fingerprint this camera_pip block by its current x= and y= values.
      // These are slot-grid coords so they differ between panels.
      const xFp = esc(cam.x);
      const yFp = esc(cam.y);

      // Helper: replace a single named numeric parameter inside the matching block.
      // Matches: <paramName>=<number> inside a camera_pip(…) block that also
      // contains x=<xFp>, y=<yFp>.
      const replaceParam = (paramName: string, newValue: number) => {
        const escapedNew = r2(newValue);
        // Match the camera_pip block containing x=<xFp> and y=<yFp>,
        // then within that block replace paramName=<anything>.
        const pattern = new RegExp(
          `(camera_pip\\([^)]*?x=${xFp}[^)]*?y=${yFp}[^)]*?)${paramName}=[\\d.]+`,
          's',
        );
        onReplaceCode(pattern, `$1${paramName}=${escapedNew}`);
      };

      // Apply each changed value in order. After each replace the source
      // changes, so the fingerprint (x/y) must be updated if x or y changed.
      // We apply x and y last so the fingerprint stays valid for earlier params.
      if (changes.width !== undefined) replaceParam('width', changes.width);
      if (changes.height !== undefined) replaceParam('height', changes.height);
      if (changes.fov !== undefined) replaceParam('fov', changes.fov);
      // Position changes: replace x and y together using the old fingerprint
      if (changes.x !== undefined || changes.y !== undefined) {
        const newX = changes.x ?? cam.x;
        const newY = changes.y ?? cam.y;
        // Replace x= using the y fingerprint as the remaining anchor
        const xPattern = new RegExp(
          `(camera_pip\\([^)]*?)x=${xFp}([^)]*?y=${yFp})`,
          's',
        );
        onReplaceCode(xPattern, `$1x=${r2(newX)}$2`);
        // Replace y= using the NEW x value as the fingerprint now
        const newXFp = esc(newX);
        const yPattern = new RegExp(
          `(camera_pip\\([^)]*?x=${newXFp}[^)]*?)y=${yFp}`,
          's',
        );
        onReplaceCode(yPattern, `$1y=${r2(newY)}`);
      }
    },
    [onReplaceCode],
  );

  /**
   * Called by PiPPanel's resize handle on pointerdown.
   * Tracks pointer movement to compute new width/height.
   *
   * Pixel-mode panels (widthPx/heightPx set): writes integer px values back.
   * Fraction-mode panels: snaps to the nearest 0.05 increment.
   */
  const handleResizeStart = useCallback(
    (index: number, startEvent: PointerEvent) => {
      if (!onReplaceCode) return;

      const cam = cameras[index];
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const containerW = rect.width;
      const containerH = rect.height;

      const startX = startEvent.clientX;
      const startY = startEvent.clientY;

      const isPxW = cam.widthPx !== undefined;
      const isPxH = cam.heightPx !== undefined;
      const isPxSize = isPxW || isPxH;

      // Starting dimensions in normalized units for live display
      const startW = resolveWidthNorm(cam, containerW);
      const startH = resolveHeightNorm(cam, containerH);

      const SNAP = 0.05;
      const MIN_W = 0.1;
      const MIN_H = 0.05;
      const MAX_W = 0.9;
      const MAX_H = 0.9;

      const snapSize = (v: number, min: number, max: number) =>
        Math.min(max, Math.max(min, Math.round(v / SNAP) * SNAP));

      snappedSizeRef.current = { width: startW, height: startH };
      setResizeState({
        index,
        width: startW,
        height: startH,
        pxMode: isPxSize,
      });

      const onMove = (e: PointerEvent) => {
        const dw = (e.clientX - startX) / containerW;
        const dh = (e.clientY - startY) / containerH;
        const newW = Math.min(MAX_W, Math.max(MIN_W, startW + dw));
        const newH = Math.min(MAX_H, Math.max(MIN_H, startH + dh));
        setResizeState({ index, width: newW, height: newH, pxMode: isPxSize });
        snappedSizeRef.current = isPxSize
          ? { width: newW, height: newH } // no snap for px mode; round on commit
          : {
              width: snapSize(newW, MIN_W, MAX_W),
              height: snapSize(newH, MIN_H, MAX_H),
            };
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);

        const snapped = snappedSizeRef.current ?? {
          width: startW,
          height: startH,
        };

        if (isPxSize) {
          // Write integer pixel values. Only write what was originally in px mode.
          const newWPx = isPxW
            ? Math.round(snapped.width * containerW)
            : undefined;
          const newHPx = isPxH
            ? Math.round(snapped.height * containerH)
            : undefined;
          const oldWRaw = isPxW ? (cam.widthPx ?? cam.width) : cam.width;
          const oldHRaw = isPxH ? (cam.heightPx ?? cam.height) : cam.height;
          const changed =
            (newWPx !== undefined && newWPx !== oldWRaw) ||
            (newHPx !== undefined && newHPx !== oldHRaw);
          if (changed) {
            handleApplyChanges(cam, {
              ...(newWPx !== undefined && { width: newWPx }),
              ...(newHPx !== undefined && { height: newHPx }),
            });
          }
        } else {
          const r2 = (n: number) => Math.round(n * 100) / 100;
          const newW = r2(snapSize(snapped.width, MIN_W, MAX_W));
          const newH = r2(snapSize(snapped.height, MIN_H, MAX_H));
          if (newW !== r2(startW) || newH !== r2(startH)) {
            handleApplyChanges(cam, { width: newW, height: newH });
          }
        }

        snappedSizeRef.current = null;
        setResizeState(null);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [cameras, onReplaceCode, handleApplyChanges],
  );

  if (cameras.length === 0) return null;

  // We need the container's current pixel dimensions to resolve px-mode panels
  // into normalized 0–1 coordinates for the slot-grid overlap check.
  // During the render pass the ref may not reflect a resize yet, but it's good
  // enough for overlap detection (the container is stable while not resizing).
  const containerRect = containerRef.current?.getBoundingClientRect();
  const containerW = containerRect?.width ?? 0;
  const containerH = containerRect?.height ?? 0;

  // Slots occupied by panels that are not currently being dragged.
  // Stored as absolute left/top so SlotGrid can compare directly.
  const occupiedSlots = cameras
    .filter((_, i) => dragState === null || dragState.index !== i)
    .map((c) => ({
      left: Math.round(resolveLeftPx(c, containerW) * 100) / 100,
      top: Math.round(resolveTopPx(c, containerH) * 100) / 100,
    }));

  const isDragging = dragState !== null;
  // Only show the slot grid for fraction-mode drags (px-mode panels drag freely)
  const showSlotGrid = isDragging && !dragState.pxMode;

  return (
    /*
     * Container spans the full viewer area but right-pads by the exclusion zone
     * so PiP panels' percentage-based coordinates never reach behind the panel.
     * overflow-hidden ensures panels that would peek into the exclusion zone are
     * clipped cleanly.
     */
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={
        rightExclusionPx > 0
          ? { paddingRight: `${rightExclusionPx}px` }
          : undefined
      }
    >
      {/* Slot grid — only visible while dragging a fraction-mode PiP */}
      {showSlotGrid &&
        (() => {
          const dc = cameras[dragState.index];
          // Use fraction dimensions for the slot grid (px panels don't show it)
          const pipW = dc.width;
          const pipH = dc.height;
          return (
            <SlotGrid
              hoveredSlot={hoveredSlot}
              occupiedSlots={occupiedSlots}
              pipW={pipW}
              pipH={pipH}
              negX={dc.x < 0}
              negY={dc.y < 0}
            />
          );
        })()}

      {cameras.map((cam, i) => {
        const isThisDragging = dragState !== null && dragState.index === i;
        const isThisResizing = resizeState !== null && resizeState.index === i;

        // While dragging, show live position; while resizing, show live dimensions.
        // During drag/resize we always switch to fraction-based rendering for the
        // live preview (clear px fields so PiPPanel uses the fractional values).
        let displayCam = cam;
        if (isThisDragging) {
          // Convert live absolute normalized position back to stored-sign fractions,
          // then clear px fields so PiPPanel renders with percentage CSS.
          displayCam = {
            ...cam,
            x: unresolveX(
              dragState.nx,
              resolveWidthNorm(cam, containerW),
              cam.x < 0,
            ),
            y: unresolveY(
              dragState.ny,
              resolveHeightNorm(cam, containerH),
              cam.y < 0,
            ),
            xPx: undefined,
            yPx: undefined,
          };
        } else if (isThisResizing) {
          // Live dimensions are already in normalized units; clear px fields.
          displayCam = {
            ...cam,
            width: resizeState.width,
            height: resizeState.height,
            widthPx: undefined,
            heightPx: undefined,
          };
        }

        return (
          <PiPPanel
            key={i}
            camera={displayCam}
            geometry={geometry}
            coloredGeometries={coloredGeometries}
            color={color}
            backgroundColor={backgroundColor}
            sceneSettings={sceneSettings}
            lights={lights}
            onCameraClick={onCameraClick}
            isDragging={isThisDragging}
            isResizing={isThisResizing}
            onDragStart={
              onReplaceCode ? (e) => handleDragStart(i, e) : undefined
            }
            onResizeStart={
              onReplaceCode ? (e) => handleResizeStart(i, e) : undefined
            }
            onApplyChanges={
              onReplaceCode
                ? (c, changes) => handleApplyChanges(c, changes)
                : undefined
            }
          />
        );
      })}
    </div>
  );
}
